import React, {
  BaseSyntheticEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Alert, LinkButton, TextInput, Button, toast } from '@pmi/dsm-react';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import { AlertInfoSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import TagsInput from 'react-tagsinput';
import { emailRegex, validDomainRegex } from '../../helpers';
import { toastWithAnalytics } from '../../analytics';
import {
  AddDomain,
  useInviteUsersMutation,
  useSummarizeInviteesMutation,
} from './apiSlice';
import { accountSelector } from '../../store/shared/accountSlice';
import styles from './wizard.module.scss';
import { CsvInvitees } from './WizardContainer';
import { ProductApiModel } from '../../models/ProductModel';
import { AgreementApiModel } from '../../models/DealModel';
import AgreementSelectedProducts from './AgreementSelectedProducts';
import useUser from '../../hooks/useUser';
import useDebounce from '../../hooks/useDebounce';

interface DomainFormProps {
  products: ProductApiModel[];
  deal: AgreementApiModel;
  onSuccess: () => void;
}

export default function DomainForm({
  deal,
  products = [],
  onSuccess,
}: DomainFormProps) {
  const user = useUser();
  const { currentAccountId } = useSelector(accountSelector);
  const [domainError, setDomainError] = useState<boolean>();
  const [showBlockInput, setShowBlockInput] = useState<boolean>();
  const [domainInput, setDomainInput] = useState<string[]>([]);
  const [excludedInput, setExcludedInput] = useState<string>('');
  const [blockEmailError, setBlockEmailError] = useState<boolean>();
  const [uploadDomainCsvError, setUploadDomainCsvError] = useState<
    string | undefined
  >();
  const [uploadExcludedCsvError, setUploadExcludedCsvError] = useState<
    string | undefined
  >();
  const [submitting, setSubmitting] = useState<boolean>();
  const [inviteUsers] = useInviteUsersMutation();
  const [domainsCsvFile, setDomainsCsvFile] = useState<string | Blob>();
  const [csvDomainInvitees, setCsvDomainInvitees] = useState<CsvInvitees>({
    valid: [],
    invalid: [],
  });
  const [csvExcludedInvitees, setCsvExcludedInvitees] = useState<CsvInvitees>({
    valid: [],
    invalid: [],
  });

  const domainUploadInputRef = useRef(null);
  const excludedUploadInputRef = useRef(null);
  const [summarizeInvitees] = useSummarizeInviteesMutation();

  const [domainTagInputEl, setDomainTagInputEl] =
    useState<HTMLInputElement | null>(null);
  const [domainInputValue, setDomainInputValue] = useState<string>('');
  const deBouncedDomainTags = useDebounce(
    domainInputValue ?? '',
    100,
  )?.toLowerCase();

  const onProceed = async () => {
    // has domainInput but it's not a valid domain
    setSubmitting(true);
    if (domainInput.length === 0) {
      setDomainError(true);
      setSubmitting(false);
      return;
    }

    const domains: string[] = [...domainInput, ...csvDomainInvitees.valid];

    // have no valid domain from both csv and input
    if (domains.length < 1) {
      setDomainError(true);
      setSubmitting(false);
      return;
    }

    // reset error if it has valid data
    setDomainError(false);

    // EXCLUSION handlers
    if (excludedInput && !emailRegex.test(excludedInput)) {
      setBlockEmailError(true);
      setSubmitting(false);
      return;
    }
    const excludedEmails = [
      ...new Set(
        excludedInput
          ? excludedInput
              .trim()
              .replace(/(\s{0,})(,|\s|;)(\s{0,})/g, ' ')
              .split(' ')
          : [],
      ),
      ...csvExcludedInvitees.valid,
    ];

    // Check if excluded emails have domains that match the allowed domains
    if (excludedEmails?.length > 0) {
      const lowerCaseDomains = domains.map((d) => d.toLowerCase());
      const hasInvalidExcludedEmail = excludedEmails.some((email) => {
        const emailDomain = email.split('@')[1]?.toLowerCase();
        return !emailDomain || !lowerCaseDomains.includes(emailDomain);
      });

      if (hasInvalidExcludedEmail) {
        setBlockEmailError(true);
        setSubmitting(false);
        return;
      }
    }

    // reset error if it has valid data
    setBlockEmailError(false);

    const addDomains: AddDomain[] = domains.map((d) => ({
      name: d.toString(),
      blacklist: excludedEmails.filter(
        (email) => email.split('@')[1]?.toLowerCase() === d.toLowerCase(),
      ),
    }));
    const productIds = products.map((p) => p.accountProductId);

    inviteUsers({
      accountId: currentAccountId,
      currentUserId: user.id,
      domains: addDomains,
      dealId: deal.id,
      emails: [],
      products: productIds,
    })
      .unwrap()
      .then(() => {
        onSuccess();
        setSubmitting(false);
      })
      .catch((e) => {
        if (e.data.title) {
          toast('Error', 'danger', e.data.title as string);
        }
        setSubmitting(false);
      });
  };

  const resetCsvDomain = () => {
    setCsvDomainInvitees({
      valid: [],
      invalid: [],
      fileName: '',
    });
  };

  const resetCsvExcluded = () => {
    setCsvExcludedInvitees({
      valid: [],
      invalid: [],
      fileName: '',
    });
  };

  const onDomainTemplateUpload = (e: BaseSyntheticEvent) => {
    const fileReader = new FileReader();
    const file = e.target.files[0] as File;
    fileReader.onload = async () => {
      if (file.type !== 'text/csv') {
        toastWithAnalytics(
          `${file.type.toString()} is not allowed. The supported template formats is .csv only`,
          'danger',
        );
      } else {
        try {
          setUploadDomainCsvError(undefined);
          setSubmitting(true);
          summarizeInvitees({
            accountId: currentAccountId,
            dealId: deal.id,
            type: 'domainInvite',
            body: {
              dealUserRequestJson: '{}',
              domainsCsvFile: file,
            },
          })
            .unwrap()
            .then((data) => {
              setDomainError(false);
              setDomainsCsvFile(file);
              setCsvDomainInvitees({
                valid: data.validCsvDomains,
                invalid: data.invalidCsvDomains,
                fileName: file.name,
              });
              setDomainInput((prev) => [
                ...new Set([...prev, ...data.validCsvDomains]),
              ]);
              setSubmitting(false);
            })
            .catch((error) => {
              setUploadDomainCsvError(
                `There was a problem with ${file.name}. ${error?.data?.title}`,
              );
              resetCsvDomain();
              setSubmitting(false);
            });
          e.target.value = '';
        } catch {
          // reset the input field, in case user wants to try again with the same file
          e.target.value = '';
          resetCsvDomain();
          setUploadDomainCsvError(
            `There was a problem with ${file.name}. Please review your file and upload it again.`,
          );
          setSubmitting(false);
        }
      }
    };

    if (file) {
      fileReader.readAsDataURL(file);
    }
  };

  const onExcludedTemplateUpload = (e: BaseSyntheticEvent) => {
    const fileReader = new FileReader();
    const file = e.target.files[0] as File;
    fileReader.onload = async () => {
      if (file.type !== 'text/csv') {
        toastWithAnalytics(
          `${file.type.toString()} is not allowed. The supported template formats is .csv only`,
          'danger',
        );
      } else {
        try {
          setUploadExcludedCsvError(undefined);
          setSubmitting(true);
          const domainsArray = [...new Set(domainInput || [])];
          summarizeInvitees({
            accountId: currentAccountId,
            dealId: deal.id,
            type: 'domainInvite',
            body: {
              dealUserRequestJson: `{domains:${JSON.stringify(domainsArray)}}`,
              excludedEmailsCsvFile: file,
              domainsCsvFile: domainsCsvFile || '',
            },
          })
            .unwrap()
            .then((data) => {
              setBlockEmailError(false);
              setCsvExcludedInvitees({
                valid: data.validCsvExcludedEmails ?? [],
                invalid: data.invalidCsvExcludedEmails ?? [],
                fileName: file.name,
              });
              setSubmitting(false);
            })
            .catch((error) => {
              setUploadExcludedCsvError(
                `There was a problem with ${file.name}. ${error?.data?.title}`,
              );
              resetCsvExcluded();
              setSubmitting(false);
            });
          e.target.value = '';
        } catch {
          // reset the input field, in case user wants to try again with the same file
          e.target.value = '';
          resetCsvExcluded();
          setUploadExcludedCsvError(
            `There was a problem with ${file.name}. Please review your file and upload it again.`,
          );
          setSubmitting(false);
        }
      }
    };

    if (file) {
      fileReader.readAsDataURL(file);
    }
  };

  const downloadTemplate = (inviteType: string) => {
    const isDomainInvite = inviteType === 'domain';
    const content = `** Maximum of 1000 ${
      isDomainInvite ? 'domains' : 'emails'
    } are supported
"** Enter one ${
      isDomainInvite ? 'domain' : 'email'
    } per cell in column A, starting directly below ""${
      isDomainInvite ? 'Domain' : 'Email'
    }"""
***************************************************
${isDomainInvite ? 'Domain' : 'Email'}
`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `Agreement_UserInvite_${
      isDomainInvite ? 'Domain' : 'Email'
    }_Template.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    setTimeout(() => {
      URL.revokeObjectURL(downloadLink.href);
      document.body.removeChild(downloadLink);
    }, 100);
  };

  const showInvalid = (emails: string[]) => {
    const firstEmails = emails.slice(0, 10);
    const restEmailsNum = emails.length - 10;
    return `${firstEmails.join(', ')}${
      restEmailsNum > 0 ? `... (plus ${restEmailsNum} more)` : ''
    }`;
  };

  const listErrors = (emails: string[]) => {
    const firstEmails = emails.slice(0, 10);
    const restEmailsNum = emails.length - 10;
    return (
      <ul className="ms-3 mb-0">
        {firstEmails.map((email) => (
          <li key={email}>{email}</li>
        ))}
        {restEmailsNum > 0 && <li>and {restEmailsNum} more</li>}
      </ul>
    );
  };

  const onTagInputChange = (tags: string[]) => {
    let temp: string[] = [];
    if (tags.length === 0) {
      setDomainInput([]);
      return;
    }
    if (tags.length === 1) {
      temp = tags[0].split(',');
    }
    tags.forEach((t) => {
      if (t.includes(',')) {
        const b = t.split(',');
        temp = [...temp, ...b.map((bb) => bb.trim())];
      } else if (validDomainRegex.test(t.trim())) {
        temp.push(t.trim());
      }
    });
    const cleaned = new Set<string>(temp.filter((t) => t.length > 0));
    if (cleaned.size > 0) {
      setDomainInput([...cleaned]);
    }
  };
  const onTagsInputKeyUp = (e: React.SyntheticEvent) => {
    const el: HTMLInputElement = e?.target as HTMLInputElement;
    if (el) {
      setDomainInputValue(el.value);
      setDomainTagInputEl(el);
    }
  };

  useEffect(() => {
    if (domainTagInputEl) {
      if (domainTagInputEl.value.includes(' ')) {
        domainTagInputEl.blur();
        domainTagInputEl.focus();
      }
    }
  }, [deBouncedDomainTags]);

  return (
    <div className="d-block" data-product-count={products.length}>
      <h3>Invite People</h3>
      <div className={`${styles.infoAlert} text-start mb-4 items-start`}>
        <AlertInfoSolidIcon fontSize="sm" className="mx-2 align-items-start" />
        <div className="ms-2">
          Enable access for your entire organization by specifying the domain
          name below. Users with matching email addresses will have access to
          the Partner Platform. For exclusions, use the provided link to block
          specific users from accessing the hub.
        </div>
      </div>
      {!!uploadDomainCsvError && (
        <div className={`text-start mt-5 ${styles.errorAlert}`}>
          <Alert
            variant="danger"
            alertText={uploadDomainCsvError}
            icon={IconDanger}
          />
        </div>
      )}
      {csvDomainInvitees.invalid?.length > 0 &&
        csvDomainInvitees.valid?.length < 1 && (
          <div className={`text-start mt-5 ${styles.errorAlert}`}>
            <Alert
              variant="danger"
              alertText={`No valid domains were found. The following entries found were invalid and not added: ${showInvalid(
                csvDomainInvitees.invalid,
              )}`}
              icon={IconDanger}
            />
          </div>
        )}
      {csvDomainInvitees.invalid?.length > 0 &&
        csvDomainInvitees.valid?.length > 0 && (
          <div className={`${styles.infoAlert} text-start mt-5 items-start`}>
            <AlertInfoSolidIcon
              fontSize="sm"
              className="mx-2 align-items-start"
            />
            <div className="ms-2">
              {csvDomainInvitees.fileName} was uploaded successfully and
              included{' '}
              <b>
                {`${csvDomainInvitees.valid.length} domain${
                  csvDomainInvitees.valid.length > 1 ? 's' : ''
                }`}
              </b>
              . The file also contained the following errors:{' '}
              {listErrors(csvDomainInvitees.invalid)}
            </div>
          </div>
        )}
      {csvDomainInvitees.invalid?.length < 1 &&
        csvDomainInvitees.valid?.length > 0 && (
          <div className={`${styles.infoAlert} text-start mt-5 items-start`}>
            <AlertInfoSolidIcon
              fontSize="sm"
              className="mx-2 align-items-start"
            />
            <div className="ms-2">
              {csvDomainInvitees.fileName} was uploaded successfully and
              included{' '}
              <b>
                {`${csvDomainInvitees.valid.length} domain${
                  csvDomainInvitees.valid.length > 1 ? 's' : ''
                }`}
                .
              </b>
            </div>
          </div>
        )}
      <hr className="mt-3 mb-4" />
      <div>
        <div className={`${styles.inviteFormContainer} gap-5`}>
          <div className="d-block flex-grow-1">
            <div className="mb-4 text-start">
              <Form.Group
                controlId="domain"
                className={domainError ? 'is-invalid' : ''}
              >
                <div data-testid="form-domain">
                  <label htmlFor="domain">Domain(s)</label>
                  <TagsInput
                    value={
                      domainInput && domainInput.length > 0 ? domainInput : []
                    }
                    onChange={onTagInputChange}
                    addOnBlur
                    onlyUnique
                    inputProps={{
                      placeholder: domainInput
                        ? 'example.com'
                        : 'Enter a valid domain',
                      id: 'domainInput',
                      onKeyUp: onTagsInputKeyUp,
                    }}
                    className={`rounded-1 border border-1 px-2 py-2 ${
                      styles.tagsInput
                    } ${
                      domainInput && domainInput.length > 0
                        ? styles.hasInputTags
                        : ''
                    } ${domainError ? 'is-invalid' : ''}`}
                    disabled={submitting}
                  />
                  <input type="submit" className="d-none" />
                  <div className="small input-comment">
                    Include everything that comes after the “@” symbol in your
                    organization’s email addresses <i>ex. mycompany.com</i>
                    <br />
                    Include multiple, comma-separated domains if needed.
                  </div>
                </div>
              </Form.Group>
            </div>

            <div className="mb-4 d-flex align-content-center small align-items-center">
              <span className="me-1 d-inline-block">Or use</span>
              <span className="d-inline-block">
                <LinkButton
                  className={`${styles.smallLinkText} p-0 small`}
                  variant="light"
                  titleText="this template"
                  onClick={() => downloadTemplate('domain')}
                  size="xs"
                />
              </span>
              <span className="d-inline-block mx-1"> and </span>
              <span className="d-inline-block">
                <LinkButton
                  className={`${styles.smallLinkText} p-0 small`}
                  variant="light"
                  titleText="upload a .csv file."
                  onClick={() => domainUploadInputRef.current.click()}
                  size="xs"
                />
              </span>
              <input
                type="file"
                name="account-logo"
                className="d-none"
                accept="text/csv"
                ref={domainUploadInputRef}
                onChange={onDomainTemplateUpload}
                data-testid="file-input"
              />
            </div>

            <div className="mb-4 text-start">
              <Button
                className="mb-4"
                variant="outline-secondary"
                titleText={`${
                  showBlockInput ? '-' : '+'
                } Exclude Specific Users`}
                onClick={() => setShowBlockInput(!showBlockInput)}
              />

              {!!uploadExcludedCsvError && (
                <div className="text-start mt-2">
                  <Alert
                    variant="danger"
                    alertText={uploadExcludedCsvError}
                    icon={IconDanger}
                  />
                </div>
              )}
              {csvExcludedInvitees.invalid?.length > 0 &&
                csvExcludedInvitees.valid?.length < 1 && (
                  <div className="text-start mt-2">
                    <Alert
                      variant="danger"
                      alertText={`No valid emails were found. The following entries found were invalid and not added: ${showInvalid(
                        csvExcludedInvitees.invalid,
                      )}`}
                      icon={IconDanger}
                    />
                  </div>
                )}
              {csvExcludedInvitees.invalid?.length > 0 &&
                csvExcludedInvitees.valid?.length > 0 && (
                  <div
                    className={`${styles.infoAlert} text-start mt-2 items-start`}
                  >
                    <AlertInfoSolidIcon
                      fontSize="sm"
                      className="mx-2 align-items-start"
                    />
                    <div className="ms-2">
                      {csvExcludedInvitees.fileName} was uploaded successfully
                      and included{' '}
                      <b>
                        {`${csvExcludedInvitees.valid.length} email${
                          csvExcludedInvitees.valid.length > 1 ? 's' : ''
                        }`}
                      </b>
                      . The file also contained the following errors:{' '}
                      {listErrors(csvExcludedInvitees.invalid)}
                    </div>
                  </div>
                )}
              {csvExcludedInvitees.invalid?.length < 1 &&
                csvExcludedInvitees.valid?.length > 0 && (
                  <div
                    className={`${styles.infoAlert} text-start mt-2 items-start`}
                  >
                    <AlertInfoSolidIcon
                      fontSize="sm"
                      className="mx-2 align-items-start"
                    />
                    <div className="ms-2">
                      {csvExcludedInvitees.fileName} was uploaded successfully
                      and included{' '}
                      <b>
                        {`${csvExcludedInvitees.valid.length} email${
                          csvExcludedInvitees.valid.length > 1 ? 's' : ''
                        }`}
                        .
                      </b>
                    </div>
                  </div>
                )}

              <Form.Group
                controlId="blockEmail"
                className={`toggle-block ${
                  // eslint-disable-next-line no-nested-ternary
                  showBlockInput
                    ? blockEmailError
                      ? 'is-invalid'
                      : ''
                    : 'hidden'
                }`}
              >
                <div data-testid="form-block-email">
                  <label htmlFor="blockEmail">Email Addresses to Exclude</label>
                  <TextInput
                    className={`me-1 ${blockEmailError ? 'is-invalid' : ''}`}
                    isInvalid={blockEmailError}
                    validationMessage="Please enter valid email(s)"
                    value={excludedInput}
                    onChange={(e: FormEvent<HTMLInputElement>) =>
                      setExcludedInput(e.currentTarget.value)
                    }
                    name="blockEmail"
                    id="blockEmail"
                    disabled={submitting}
                  />
                  <input type="submit" className="d-none" />
                  <div className="small input-comment">
                    These users will not be able to access your Partner
                    Platform.
                    <br />
                    Include multiple, comma-separated emails to block more than
                    one person.
                  </div>
                </div>
              </Form.Group>
            </div>

            {showBlockInput && (
              <div className="mb-4 d-flex align-content-center align-items-center small">
                <span className="d-inline-block me-1">Or use</span>
                <span className="d-inline-block ">
                  <LinkButton
                    className={`${styles.smallLinkText} p-0 small`}
                    variant="light"
                    titleText="this template"
                    onClick={() => downloadTemplate('excluded')}
                    size="xs"
                  />
                </span>
                <span className="d-inline-block mx-1"> and </span>
                <span className="d-inline-block">
                  <LinkButton
                    className={`${styles.smallLinkText} p-0 small`}
                    variant="light"
                    titleText="upload a .csv file."
                    onClick={() => excludedUploadInputRef.current.click()}
                    size="xs"
                  />
                </span>
                <input
                  type="file"
                  name="account-logo"
                  className="d-none"
                  accept="text/csv"
                  ref={excludedUploadInputRef}
                  onChange={onExcludedTemplateUpload}
                  data-testid="file-input"
                />
              </div>
            )}
          </div>
          <div className="d-block">
            {products && products.length > 0 && (
              <AgreementSelectedProducts
                products={products}
                deal={deal}
                onClick={onProceed}
                disabled={domainInput.length === 0 || submitting}
                buttonText="Allow Company Access to Products"
                isInviteStep
              />
            )}
          </div>
        </div>
      </div>
      <div
        className="d-none d-md-block d-sm-block"
        style={{ paddingBottom: '270px' }}
      />
      <div className="d-block d-sm-none" style={{ paddingBottom: '210px' }} />
    </div>
  );
}
